import { } from 'react-redux';

import { Action } from '@reduxjs/toolkit';

import * as actionTypes from '../constants/actionTypes';

import { updateObject } from '../../../../shared/utils';

import { AppAction } from '../../../../store';

import {
  SystemMessageType,
  KeyValuePair,
  SystemMessage
} from '../../../../shared/models';

export type SystemMessageEditState = {
  systemMessageTypes: SystemMessageType[] | null;
  systemMessageTypesError: string | null;
  systemMessageTypesLoading: boolean;

  databases: KeyValuePair[] | null;
  databasesError: string | null;
  databasesLoading: boolean;

  systemMessageData: SystemMessage | null;
  systemMessageDataError: string | null;
  systemMessageDataLoading: boolean;

  saveSystemMessage: SystemMessage | null;
  saveSystemMessageError: string | null;
  saveSystemMessageLoading: boolean;

  saveSystemMessageImage: string | null;
  saveSystemMessageImageError: string | null;
  saveSystemMessageImageLoading: boolean;
}

const initialState: SystemMessageEditState = {
  systemMessageTypes: null,
  systemMessageTypesError: null,
  systemMessageTypesLoading: false,

  databases: null,
  databasesError: null,
  databasesLoading: false,

  systemMessageData: null,
  systemMessageDataError: null,
  systemMessageDataLoading: false,

  saveSystemMessage: null,
  saveSystemMessageError: null,
  saveSystemMessageLoading: false,

  saveSystemMessageImage: null,
  saveSystemMessageImageError: null,
  saveSystemMessageImageLoading: false,
};

const getSystemMessageTypesStart = (state: SystemMessageEditState, action: AppAction<SystemMessageType[]>): SystemMessageEditState => {
  return updateObject(state, {
    systemMessageTypesError: null,
    systemMessageTypesLoading: true,
  });
};

const getSystemMessageTypesSuccess = (state: SystemMessageEditState, action: AppAction<SystemMessageType[]>): SystemMessageEditState => {
  return updateObject(state, {
    systemMessageTypes: action.data,
    systemMessageTypesError: null,
    systemMessageTypesLoading: false,
  });
};

const getSystemMessageTypesFail = (state: SystemMessageEditState, action: AppAction<SystemMessageType[]>): SystemMessageEditState => {
  return updateObject(state, {
    databases: null,
    databasesError: action.error,
    databasesLoading: false,
  });
};

const getDatabasesStart = (state: SystemMessageEditState, action: AppAction<KeyValuePair[]>): SystemMessageEditState => {
  return updateObject(state, {
    databasesError: null,
    databasesLoading: true,
  });
};

const getDatabasesSuccess = (state: SystemMessageEditState, action: AppAction<KeyValuePair[]>): SystemMessageEditState => {
  return updateObject(state, {
    databases: action.data,
    databasesError: null,
    databasesLoading: false,
  });
};

const getDatabasesFail = (state: SystemMessageEditState, action: AppAction<KeyValuePair[]>): SystemMessageEditState => {
  return updateObject(state, {
    databases: null,
    databasesError: action.error,
    databasesLoading: false,
  });
};

const getSystemMessageDataStart = (state: SystemMessageEditState, action: AppAction<SystemMessage>): SystemMessageEditState => {
  return updateObject(state, {
    systemMessageDataError: null,
    systemMessageDataLoading: true,
  });
};

const getSystemMessageDataSuccess = (state: SystemMessageEditState, action: AppAction<SystemMessage>): SystemMessageEditState => {
  return updateObject(state, {
    systemMessageData: action.data,
    systemMessageDataError: null,
    systemMessageDataLoading: false,
  });
};

const getSystemMessageDataFail = (state: SystemMessageEditState, action: AppAction<SystemMessage>): SystemMessageEditState => {
  return updateObject(state, {
    systemMessageData: null,
    systemMessageDataError: action.error,
    systemMessageDataLoading: false,
  });
};

const saveSystemMessageStart = (state: SystemMessageEditState, action: AppAction<SystemMessage>): SystemMessageEditState => {
  return updateObject(state, {
    saveSystemMessageError: null,
    saveSystemMessageLoading: true,
  });
};

const saveSystemMessageSuccess = (state: SystemMessageEditState, action: AppAction<SystemMessage>): SystemMessageEditState => {
  return updateObject(state, {
    saveSystemMessage: action.data,
    saveSystemMessageError: null,
    saveSystemMessageLoading: false,
  });
};

const saveSystemMessageFail = (state: SystemMessageEditState, action: AppAction<SystemMessage>): SystemMessageEditState => {
  return updateObject(state, {
    saveSystemMessage: null,
    saveSystemMessageError: action.error,
    saveSystemMessageLoading: false,
  });
};

const saveSystemMessageImageStart = (state: SystemMessageEditState, action: AppAction<string>): SystemMessageEditState => {
  return updateObject(state, {
    saveSystemMessageImageError: null,
    saveSystemMessageImageLoading: true,
  });
};

const saveSystemMessageImageSuccess = (state: SystemMessageEditState, action: AppAction<string>): SystemMessageEditState => {
  return updateObject(state, {
    saveSystemMessageImage: action.data,
    saveSystemMessageImageError: null,
    saveSystemMessageImageLoading: false,
  });
};

const saveSystemMessageImageFail = (state: SystemMessageEditState, action: AppAction<string>): SystemMessageEditState => {
  return updateObject(state, {
    saveSystemMessageImage: null,
    saveSystemMessageImageError: action.error,
    saveSystemMessageImageLoading: false,
  });
};

const reducer = (state = initialState, action: Action): SystemMessageEditState => {
  switch (action.type) {
    case actionTypes.SYSTEM_MESSAGES_GET_SYSTEM_MESSAGES_TYPES_START:
      return getSystemMessageTypesStart(state, action as AppAction<SystemMessageType[]>);
    case actionTypes.SYSTEM_MESSAGES_GET_SYSTEM_MESSAGES_TYPES_SUCCESS:
      return getSystemMessageTypesSuccess(state, action as AppAction<SystemMessageType[]>);
    case actionTypes.SYSTEM_MESSAGES_GET_SYSTEM_MESSAGES_TYPES_FAIL:
      return getSystemMessageTypesFail(state, action as AppAction<SystemMessageType[]>);

    case actionTypes.SYSTEM_MESSAGES_GET_DATABASES_START:
      return getDatabasesStart(state, action as AppAction<KeyValuePair[]>);
    case actionTypes.SYSTEM_MESSAGES_GET_DATABASES_SUCCESS:
      return getDatabasesSuccess(state, action as AppAction<KeyValuePair[]>);
    case actionTypes.SYSTEM_MESSAGES_GET_DATABASES_FAIL:
      return getDatabasesFail(state, action as AppAction<KeyValuePair[]>);

    case actionTypes.SYSTEM_MESSAGES_GET_SYSTEM_MESSAGE_DATA_START:
      return getSystemMessageDataStart(state, action as AppAction<SystemMessage>);
    case actionTypes.SYSTEM_MESSAGES_GET_SYSTEM_MESSAGE_DATA_SUCCESS:
      return getSystemMessageDataSuccess(state, action as AppAction<SystemMessage>);
    case actionTypes.SYSTEM_MESSAGES_GET_SYSTEM_MESSAGE_DATA_FAIL:
      return getSystemMessageDataFail(state, action as AppAction<SystemMessage>);

    case actionTypes.SYSTEM_MESSAGES_SAVE_SYSTEM_MESSAGE_START:
      return saveSystemMessageStart(state, action as AppAction<SystemMessage>);
    case actionTypes.SYSTEM_MESSAGES_SAVE_SYSTEM_MESSAGE_SUCCESS:
      return saveSystemMessageSuccess(state, action as AppAction<SystemMessage>);
    case actionTypes.SYSTEM_MESSAGES_SAVE_SYSTEM_MESSAGE_FAIL:
      return saveSystemMessageFail(state, action as AppAction<SystemMessage>);

    case actionTypes.SYSTEM_MESSAGES_SAVE_SYSTEM_MESSAGE_IMAGE_START:
      return saveSystemMessageImageStart(state, action as AppAction<string>);
    case actionTypes.SYSTEM_MESSAGES_SAVE_SYSTEM_MESSAGE_IMAGE_SUCCESS:
      return saveSystemMessageImageSuccess(state, action as AppAction<string>);
    case actionTypes.SYSTEM_MESSAGES_SAVE_SYSTEM_MESSAGE_IMAGE_FAIL:
      return saveSystemMessageImageFail(state, action as AppAction<string>);

    default:
      return state;
  }
};

export default reducer;
